/**
 * The app's only network surface (SPEC 10: no calls except to our own functions).
 *
 * - roster: static /roster.json, cached in memory
 * - story: POST /.netlify/functions/story, with a localStorage story cache so a
 *   pair the kid has already seen works offline (SPEC 12). A 5xx is asked once
 *   more after a pause: the gateway gives up on a slow story before the
 *   function does, and the story is usually cached by then (D-179)
 * - prefetchStory: the same call, started the moment both animals are picked,
 *   so "Fight!" usually finds the story already here (D-086)
 * - requestAnimal: the off-roster stub (SPEC 9)
 */

// v3 (D-099): the app writes the fact pages and the model quizzes on them; a v2
// story's quiz could lean on model-written pages the kid no longer sees.
// v4 (D-155 – D-157): the rules moved some winners and a close fight is now
// rolled per battle, so a v3 story could disagree with the arithmetic the
// server would put under it today. Left behind; a replayed pair fetches once.
const STORY_KEY = 'ab.stories.v4'
const STORY_LIMIT = 60 // most recent pairs kept on device

let rosterPromise = null
/**
 * Each animal's record revision from roster.json (D-111). A story cached on the
 * device is keyed to it, so a corrected record — the cheetah's speed, D-110 —
 * is never read back stale. Filled before anything renders: App.jsx waits on
 * loadRoster() before the first screen.
 */
let revOf = new Map()

export function loadRoster() {
  if (!rosterPromise) {
    rosterPromise = fetch('/roster.json')
      .then((r) => {
        if (!r.ok) throw new Error('roster')
        return r.json()
      })
      .then((r) => {
        revOf = new Map((r.animals || []).map((x) => [x.slug, x.rev || '']))
        return r
      })
      .catch((err) => {
        rosterPromise = null
        throw err
      })
  }
  return rosterPromise
}

function readStoryCache() {
  try {
    return JSON.parse(localStorage.getItem(STORY_KEY) || '{}')
  } catch {
    return {}
  }
}

function writeStoryCache(cache) {
  try {
    // Trim to the most recent entries so the cache cannot grow without bound.
    const entries = Object.entries(cache).sort((x, y) => y[1].at - x[1].at).slice(0, STORY_LIMIT)
    localStorage.setItem(STORY_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // Storage full or blocked: caching is a convenience, never a failure.
  }
}

export const storyCacheKey = (a, b, age) => `${a}__${b}__${age}__${revOf.get(a) || ''}${revOf.get(b) || ''}`

/**
 * A rolled fight (D-157): the server flipped a coin inside the close band, and
 * the story on the device is one way it went. It is served from the device
 * once — the prefetch-to-Fight! handoff — and after that the pair goes back to
 * the server for a fresh roll; the played copy stays only as the offline story.
 */
const rolled = (hit) => !!hit?.numbers?.coin

/**
 * The story on the device for this pair, or null when there is none — or when
 * the one there is a rolled fight the kid has already played.
 * @returns {{story: object, numbers: object|null}|null}
 */
export function cachedStory(a, b, age) {
  const hit = readStoryCache()[storyCacheKey(a, b, age)]
  if (!hit?.story || (rolled(hit) && hit.served)) return null
  return { story: hit.story, numbers: hit.numbers || null }
}

/** `cachedStory`, and a rolled fight is marked as played on the way out. */
export function takeStory(a, b, age) {
  const hit = cachedStory(a, b, age)
  if (hit) markServed(storyCacheKey(a, b, age))
  return hit
}

function markServed(key) {
  const cache = readStoryCache()
  if (!rolled(cache[key]) || cache[key].served) return
  cache[key] = { ...cache[key], served: true }
  writeStoryCache(cache)
}

/** The last story on the device for this pair, played or not: the offline fallback. */
function playedStory(a, b, age) {
  const hit = readStoryCache()[storyCacheKey(a, b, age)]
  return hit?.story ? { story: hit.story, numbers: hit.numbers || null, fromCache: true } : null
}

/**
 * Prefetches in progress, keyed by pair (D-086). Only `prefetchStory` registers
 * here, and deliberately so: a prefetch belongs to nobody, so it is safe to
 * share, while a request the caller can abort must not be — cancelling it would
 * cancel it out from under whoever else joined. A caller that joins a prefetch
 * therefore cannot cancel the network, only stop listening, which is what
 * App.jsx's own `signal.aborted` guard already does.
 */
const inflight = new Map()

/**
 * Speech is parsed on the device (App.jsx, D-099), so every request names a
 * pair; the story function's own `speech` input stays for the Phase 2 audio box.
 *
 * @param {{a: string, b: string, age: string, signal?: AbortSignal}} req
 * @returns {Promise<{story: object, numbers: object|null, fromCache: boolean}>}
 */
export function fetchStory({ a, b, age, signal }) {
  // Already on the device: no wait, no request. A prefetch that finished
  // before the kid tapped Fight! lands here, which is the point of it.
  const hit = takeStory(a, b, age)
  if (hit) return Promise.resolve({ ...hit, fromCache: true })
  // Whatever arrives is played now, so a rolled fight is marked as it lands.
  const key = storyCacheKey(a, b, age)
  const served = (res) => { markServed(key); return res }
  // A prefetch for this pair is still running: join it rather than ask twice.
  const running = inflight.get(key)
  if (running) return running.then(served)
  return requestStory({ a, b, age, signal }).then(served)
}

/**
 * Start the story for a picked pair and put it on the device. Nothing waits on
 * the result: `fetchStory` finds it in the cache, or joins this request if the
 * kid got to Fight! first. Never given a signal — see `inflight` above.
 */
export function prefetchStory({ a, b, age }) {
  if (!a || !b) return
  const key = storyCacheKey(a, b, age)
  if (inflight.has(key) || cachedStory(a, b, age)) return
  const p = requestStory({ a, b, age }).finally(() => inflight.delete(key))
  inflight.set(key, p)
  p.catch(() => {
    // The tap on Fight! will make its own request and surface the error there.
  })
}

/**
 * One more ask after a gateway failure (D-179). Netlify's gateway stops waiting
 * for a slow story at about 30 s and answers 504 while the function runs on,
 * finishes, and caches what it wrote — so a story that "failed" is usually on
 * the server a few seconds later. Long enough for that write to land; short
 * enough that the kid is still at the battle door.
 */
export const RETRY_AFTER_MS = 3000

const abortError = () => Object.assign(new Error('aborted'), { name: 'AbortError' })

/** A pause the caller can cancel: resolves after `ms`, rejects with an AbortError if `signal` fires first. */
function pause(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError())
    let t = null
    const onAbort = () => { clearTimeout(t); reject(abortError()) }
    t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve() }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

async function requestStory({ a, b, age, signal }) {
  for (let ask = 1; ; ask++) {
    let res
    try {
      res = await fetch('/.netlify/functions/story', {
        method: 'POST',
        signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ a, b, age_band: age }),
      })
    } catch (err) {
      // An abort is the caller changing its mind (the kid picked a different
      // animal); it is not an error to show anyone, so it is re-thrown as-is for
      // the caller to swallow. Anything else here means no response at all —
      // offline, DNS — and the browser's own "Failed to fetch" is not something
      // to put in front of a 4-year-old.
      if (err?.name === 'AbortError') throw err
      // Offline. A rolled fight the device has already played is still a story
      // (SPEC 12: a pair the kid has seen works offline) — the last way it went
      // beats "Are you online?".
      const played = playedStory(a, b, age)
      if (played) return played
      throw new Error('Could not reach the story maker. Are you online?')
    }
    let body = null
    try {
      body = await res.json()
    } catch {
      body = null
    }
    if (res.ok && body?.story) {
      const numbers = body.numbers || null
      const cache = readStoryCache()
      cache[storyCacheKey(a, b, age)] = { story: body.story, numbers, at: Date.now() }
      writeStoryCache(cache)
      return { story: body.story, numbers, fromCache: false, meta: body.meta }
    }
    // The server down rather than the request wrong: the same fallback as offline.
    const played = res.status >= 500 ? playedStory(a, b, age) : null
    if (played) return played
    // A 5xx is the gateway or the platform giving up, never the pair — a pair
    // the server cannot use is a 400 — and the story it gave up on is most
    // likely in the server's cache by now. One more ask, after a pause; a
    // second 5xx is the error.
    if (res.status >= 500 && ask === 1) {
      await pause(RETRY_AFTER_MS, signal)
      continue
    }
    // Netlify's own 504 is an HTML page with no `error` in it: the generic line.
    const err = new Error(body?.error || 'Could not build that story.')
    err.status = res.status
    throw err
  }
}

export async function requestAnimal(name) {
  try {
    await fetch('/.netlify/functions/request_animal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name }),
    })
  } catch {
    // Best effort; the kid never needs to know.
  }
}

let creditsPromise = null
/**
 * /photo_credits.json — every Commons photo's author, license and source link
 * (D-143). Static like the roster, and fetched only when the credits screen is
 * opened: nothing on the way to a battle needs it, so it is not part of the
 * first paint's 315 KB.
 */
export function loadPhotoCredits() {
  if (!creditsPromise) {
    creditsPromise = fetch('/photo_credits.json')
      .then((r) => {
        if (!r.ok) throw new Error('credits')
        return r.json()
      })
      .catch((err) => {
        creditsPromise = null // a failed fetch must be retryable
        throw err
      })
  }
  return creditsPromise
}
